import { language } from '@models/settings';
import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, QueryList, ViewChildren } from '@angular/core';
import { SectionElement } from '@components/anchor-menu/anchor-menu.component';
import { DutyEngineerControlMode } from 'icreate/common/models/duty-engineer-settings';
import { SoftwareSpecification } from 'icreate/common/models/system-specification';
import { Subject, fromEvent, takeUntil } from 'rxjs';
import { commonLanguages } from 'icreate/common/models/common';

@Component({
    selector: 'app-software-specifications',
    templateUrl: './software-specifications.component.html',
    styleUrl: './software-specifications.component.css',
    standalone: false,
})
export class SoftwareSpecificationsComponent {
    @Input() softwareSpecification: SoftwareSpecification;
    @ViewChildren('section') sectionElementList: QueryList<ElementRef>;

    constructor(private cdr: ChangeDetectorRef) {}

    currentSectionIndex: number = 0;

    sectionsList: SectionElement[] = [
        { title: 'system-specifications.display.title', index: 0 },
        { title: 'system-specifications.clock.title', index: 1 },
        { title: 'system-specifications.duty-settings.title', index: 2 },
    ];

    private isScrollToFunction: boolean = false;
    private destroy$: Subject<void> = new Subject();

    @HostListener('scroll', [])
    @HostListener('window:scroll', [])
    onScroll(): void {
        this.updateScrollIndex();
    }

    ngOnInit() {
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.updateScrollIndex();
                });

            fromEvent(scrollContainer, 'scrollend')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isScrollToFunction = false;
                });
        }
    }

    ngAfterViewInit(): void {
        this.updateScrollIndex();
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleScrollSection(sectionIndex: number) {
        if (!this.sectionElementList) return;
        const sections = this.sectionElementList.toArray();
        const selectedSection = sections[sectionIndex];
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');

        if (!selectedSection || !scrollContainer) {
            return;
        }

        const elementRect: DOMRect = selectedSection.nativeElement.getBoundingClientRect();
        const containerRect: DOMRect = scrollContainer.getBoundingClientRect();

        const offsetWithinContainer = elementRect.top - containerRect.top + scrollContainer.scrollTop - 10;
        this.currentSectionIndex = sectionIndex;
        this.isScrollToFunction = true;
        scrollContainer.scrollTo({ top: offsetWithinContainer, behavior: 'smooth' });
        this.cdr.markForCheck();
    }

    get hasChanges() {
        return false;
    }

    get displayData() {
        return this.softwareSpecification.display;
    }

    get clockData() {
        return this.softwareSpecification.clock;
    }

    get language() {
        const language = this.softwareSpecification.display.language;
        if (language == commonLanguages.EN) return 'dlg.english';
        else if (language == commonLanguages.JP) return 'dlg.japanese';
        else return '';
    }

    get dutySettingsControl() {
        if (this.softwareSpecification.dutySettings == DutyEngineerControlMode.OPS_CONTROL) {
            return 'system-specifications.duty-settings.OPS';
        } else if (this.softwareSpecification.dutySettings == DutyEngineerControlMode.CONSOLE_CONTROL) {
            return 'system-specifications.duty-settings.console';
        }
        return '';
    }

    private updateScrollIndex() {
        if (!this.sectionElementList || this.isScrollToFunction) return;
        const sections = this.sectionElementList.toArray();
        if (!sections.length) return;

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');

        const atBottom = window.innerHeight + scrollContainer.scrollTop >= scrollContainer.scrollHeight - 5;

        if (atBottom) {
            this.currentSectionIndex = sections.length - 1;
            this.cdr.markForCheck();
            return;
        }

        const viewportHeight = window.innerHeight;
        let bestIndex = 0;

        const getVisibleHeight = (rect: DOMRect): number => {
            const visibleTop = Math.max(rect.top, 0);
            const visibleBottom = Math.min(rect.bottom, viewportHeight);
            return Math.max(0, visibleBottom - visibleTop);
        };

        for (let i = 0; i < sections.length; i++) {
            const section = sections[i];
            const rect: DOMRect = section.nativeElement.getBoundingClientRect();

            if (rect.y > 0 && i == 0) {
                bestIndex = i;
                break;
            }
            if (i == 0) continue;
            const previousSection = sections[i - 1];
            const previousSectionRect: DOMRect = previousSection?.nativeElement?.getBoundingClientRect();

            const currentVisibleHeight = getVisibleHeight(rect);
            const previousVisibleHeight = getVisibleHeight(previousSectionRect);

            if (previousVisibleHeight > currentVisibleHeight) {
                bestIndex = i - 1;
                break;
            } else if (rect.y > 0) {
                bestIndex = i;
                break;
            }
        }

        this.currentSectionIndex = bestIndex;
        this.cdr.markForCheck();
    }
}
